// Microsoft Graph: Outlook-Kalender lesen, um den aktuell laufenden Termin (und die
// heutigen Termine) samt Teilnehmern zu erkennen.
//
// Läuft jetzt direkt im Browser statt über den Server: Der Nutzer hat sich ohnehin per
// MSAL angemeldet und Graph erlaubt Aufrufe aus dem Browser. Das spart den Umweg über
// die eigene API und die dafür nötige On-Behalf-Of-Delegierung.
// Die Auswertungslogik ist unverändert aus der Electron-Version übernommen.
import { getGraphToken } from './msal';

async function graphGet(pathAndQuery) {
  const token = await getGraphToken();
  const res = await fetch(`https://graph.microsoft.com/v1.0${pathAndQuery}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      // Termintext als Klartext statt HTML anfordern – so ist er direkt speicherbar
      Prefer: 'outlook.timezone="UTC", outlook.body-content-type="text"',
    },
  });
  if (!res.ok) {
    const body = await res.text();
    if (body.includes('MailboxNotEnabledForRESTAPI')) {
      throw new Error(
        'Das angemeldete Konto hat kein Exchange-Online-Postfach (evtl. On-Premise-Exchange ' +
          'oder Konto ohne Mailbox). Bitte mit einem Konto mit Microsoft-365-Postfach anmelden.'
      );
    }
    throw new Error(`Microsoft Graph ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json();
}

function mapEvent(ev) {
  const attendees = (ev.attendees || [])
    .map((a) => ({
      name: (a.emailAddress && a.emailAddress.name) || '',
      email: ((a.emailAddress && a.emailAddress.address) || '').toLowerCase(),
    }))
    .filter((a) => a.email);
  // Organisator ergänzen, falls nicht ohnehin in der Teilnehmerliste
  const org = ev.organizer && ev.organizer.emailAddress;
  if (org && org.address && !attendees.some((a) => a.email === org.address.toLowerCase())) {
    attendees.unshift({ name: org.name || '', email: org.address.toLowerCase() });
  }
  return {
    id: ev.id,
    subject: ev.subject || '(ohne Betreff)',
    start: ev.start && ev.start.dateTime ? ev.start.dateTime + 'Z' : null,
    end: ev.end && ev.end.dateTime ? ev.end.dateTime + 'Z' : null,
    isOnline: !!ev.isOnlineMeeting,
    // Outlook-Kategorien des Termins (leer, wenn nicht kategorisiert)
    categories: ev.categories || [],
    // Termintext (Einladungstext/Agenda) und Ort – werden mit der Aufnahme gespeichert
    body: ev.body && ev.body.content ? ev.body.content.trim() : '',
    location: (ev.location && ev.location.displayName) || '',
    organizer: org ? { name: org.name || '', email: (org.address || '').toLowerCase() } : null,
    attendees,
  };
}

// Termine des heutigen Tages (UTC-Fenster), nach Startzeit sortiert
export async function getTodaysMeetings() {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const q =
    `/me/calendarView?startDateTime=${start.toISOString()}&endDateTime=${end.toISOString()}` +
    `&$select=subject,start,end,attendees,organizer,isOnlineMeeting,location,body,categories` +
    `&$orderby=start/dateTime&$top=50`;
  const data = await graphGet(q);
  return (data.value || []).map(mapEvent);
}

// Vorauswahl für die Terminliste: der gerade laufende Termin, sonst der nächste
// des Tages – unabhängig davon, wie weit er noch entfernt ist.
//
// Früher galt hier ein Fenster von 10 Minuten. Das führte dazu, dass die Auswahl
// meist leer blieb: Wer eine Aufnahme vorbereitet, tut das oft früher, und dann
// musste der Termin von Hand gewählt werden.
export function findCurrentMeeting(meetings) {
  const now = Date.now();

  const running = meetings.find(
    (m) => m.start && m.end && new Date(m.start).getTime() <= now && now <= new Date(m.end).getTime()
  );
  if (running) return running;

  // getTodaysMeetings liefert bereits nach Startzeit sortiert – der erste noch
  // ausstehende Termin ist damit der nächste.
  const next = meetings.find((m) => m.start && new Date(m.start).getTime() > now);
  return next || null;
}
